import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Boxes, Globe, Handshake, ShieldCheck, Truck, Users, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import PageHead from '@/components/PageHead';
import { useQuote } from '@/context/QuoteContext';

const FACTS = [
  { icon: Boxes, label: '+1000 références', desc: 'Multi-catégories' },
  { icon: Users, label: 'B2B uniquement', desc: 'Supermarchés & détaillants' },
  { icon: Globe, label: 'France entière', desc: 'Distribution agile' },
  { icon: Award, label: 'Marques reconnues', desc: 'Apple, Samsung, Smeg…' },
];

const VALUES = [
  { icon: ShieldCheck, t: 'Qualité sans compromis', d: 'Nous testons chaque référence avant inscription au catalogue.' },
  { icon: Handshake, t: 'Relation de confiance', d: 'Un interlocuteur unique, une réponse claire et rapide.' },
  { icon: Truck, t: 'Logistique millimétrée', d: 'Cadencement régulier, palettisation à la demande.' },
];

export default function AboutPage() {
  const { openQuote } = useQuote();
  return (
    <div data-testid="about-page">
      <PageHead title="L'entreprise" description="ALOFI SA — supermarché Intermarché à Marguerittes. Grossiste & détaillant, distribution alimentaire et non alimentaire pour professionnels." />
      <section className="hero-bg relative noise-overlay">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <Badge variant="outline" className="tracking-[0.08em] uppercase text-[10px] bg-white/80">L’entreprise</Badge>
          <h1 className="mt-3 font-heading text-3xl sm:text-5xl font-semibold tracking-tight max-w-3xl text-balance">
            ALOFI SA — supermarché Intermarché, grossiste & détaillant.
          </h1>
          <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">
            Spécialiste de la distribution de produits alimentaires et non alimentaires sous l’enseigne <strong>Intermarché</strong>, ALOFI SA accompagne les professionnels avec une gamme très large, soigneusement sélectionnée, aux meilleurs prix.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-3">
        {FACTS.map(({ icon: Icon, label, desc }) => (
          <Card key={label} className="rounded-2xl border-border p-5">
            <div className="w-10 h-10 rounded-xl bg-[hsl(186_45%_92%)] flex items-center justify-center mb-3">
              <Icon className="w-5 h-5 text-[hsl(186_55%_38%)]" />
            </div>
            <div className="font-heading font-semibold">{label}</div>
            <div className="text-xs text-muted-foreground mt-1">{desc}</div>
          </Card>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-2 gap-10 items-center">
        <div className="rounded-3xl overflow-hidden border border-border">
          <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1100&q=80" alt="Entrepôt" className="w-full h-full object-cover" />
        </div>
        <div>
          <Badge variant="outline" className="tracking-[0.08em] uppercase text-[10px]">Notre mission</Badge>
          <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight">Approvisionner. Faire grandir vos rayons.</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            Avec une large gamme de références, nous accompagnons les professionnels dans l’approvisionnement
            de leurs rayons avec des produits de qualité aux meilleurs prix. Notre mission est simple :
            vous donner les moyens d’offrir le meilleur à vos clients, sans compromis.
          </p>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            Nous travaillons avec des marques internationales reconnues (Apple, Samsung, Smeg, Philips, Dyson, Tefal…)
            et des spécialistes locaux pour les segments alimentaires et premium.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl font-semibold tracking-tight">Nos valeurs</h2>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {VALUES.map(({ icon: Icon, t, d }) => (
            <Card key={t} className="rounded-2xl border-border p-6">
              <div className="w-10 h-10 rounded-xl bg-[hsl(186_45%_92%)] flex items-center justify-center mb-3">
                <Icon className="w-5 h-5 text-[hsl(186_55%_38%)]" />
              </div>
              <h3 className="font-heading font-semibold">{t}</h3>
              <p className="text-sm text-muted-foreground mt-1">{d}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="rounded-3xl border-border p-8 sm:p-10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Travaillons ensemble</h2>
            <p className="mt-2 text-muted-foreground">Vous êtes supermarché, commerce de détail ou revendeur ? Nous vous accompagnons.</p>
          </div>
          <div className="md:justify-self-end flex gap-3">
            <Button onClick={() => openQuote()} size="lg" className="rounded-xl" data-testid="about-cta-quote">Demander un devis <ArrowRight className="w-4 h-4 ml-2" /></Button>
            <Button asChild variant="secondary" size="lg" className="rounded-xl"><Link to="/contact">Nous contacter</Link></Button>
          </div>
        </Card>
      </section>
    </div>
  );
}
